/**
 * The last five opportunities, kept where a failed fetch can still read them.
 *
 * §J's first requirement, and the cheapest one: "cache currently opened
 * opportunity information". This is localStorage and about a hundred lines. It
 * needs no service worker, no cache API and no offline-first architecture —
 * `studio42.config.json` says `offline: false` and that stays true.
 *
 * ── Why five, and why localStorage ───────────────────────────────────────────
 *
 * The field pattern is: look at a shortlist at the house on wifi, drive out,
 * open one of them in a dead spot. Five covers a morning's shortlist. Keeping
 * more is not free — localStorage is a synchronous API on the main thread and
 * every write serialises the index — and the sixth-oldest opportunity is not
 * what anybody is standing in front of.
 *
 * localStorage rather than IndexedDB because a read has to be SYNCHRONOUS to be
 * useful here: the screen wants to render cached content on its first paint,
 * not after an await that flashes an empty state first.
 *
 * ── The banner is not optional ───────────────────────────────────────────────
 *
 * Everything served from here is stale by definition, and a stale asking price
 * is exactly the number somebody is about to say out loud to a seller. `savedAt`
 * comes back with every read specifically so the screen can show
 * "showing data from 09:14 — offline" and never present cache as live.
 */

const INDEX_KEY = 'hh.opp.index'
const PREFIX = 'hh.opp.'
export const MAX_CACHED = 5

export interface CachedOpportunity<T = unknown> {
  id: string
  /** Epoch millis when this was written. The banner reads it. */
  savedAt: number
  payload: T
}

function store(): Storage | null {
  try {
    // Touched, not merely read: Safari's private mode used to expose
    // localStorage and throw on setItem, and a null check would have missed it.
    const s = localStorage
    const probe = '__hh_probe__'
    s.setItem(probe, '1')
    s.removeItem(probe)
    return s
  } catch {
    // A private window, or storage disabled entirely. The application works,
    // it just has no memory between page loads.
    return null
  }
}

function readIndex(s: Storage): string[] {
  try {
    const raw = s.getItem(INDEX_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    // A corrupt index is recoverable by forgetting it; the payload rows it
    // pointed at become garbage that `prune` clears on the next write.
    return []
  }
}

function writeIndex(s: Storage, ids: string[]): void {
  try {
    s.setItem(INDEX_KEY, JSON.stringify(ids))
  } catch {
    /* Out of quota while writing a list of five strings means storage is gone;
       the cache degrades to nothing, which is the designed worst case. */
  }
}

/** Newest first. Exported so a screen can offer "recently viewed" with no network. */
export function list(): string[] {
  const s = store()
  return s ? readIndex(s) : []
}

export function get<T>(id: string): CachedOpportunity<T> | null {
  const s = store()
  if (!s) return null
  try {
    const raw = s.getItem(PREFIX + id)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedOpportunity<T>
    if (!parsed || typeof parsed.savedAt !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Write one opportunity payload and make it the most recent.
 *
 * Returns false when nothing could be stored, so a caller is never told a cache
 * exists that does not. A quota error evicts the oldest and retries once —
 * a single photo-heavy payload can be most of the 5MB budget on its own.
 */
export function put<T>(id: string, payload: T, now = Date.now()): boolean {
  const s = store()
  if (!s) return false

  const record: CachedOpportunity<T> = { id, savedAt: now, payload }
  const serialised = JSON.stringify(record)

  let ids = [id, ...readIndex(s).filter((x) => x !== id)]
  for (const stale of ids.slice(MAX_CACHED)) {
    try {
      s.removeItem(PREFIX + stale)
    } catch {
      /* nothing to do about a remove that fails; the index drops it anyway */
    }
  }
  ids = ids.slice(0, MAX_CACHED)

  try {
    s.setItem(PREFIX + id, serialised)
  } catch {
    // Quota. Drop the oldest OTHER entry and try once more. Not a loop: if two
    // evictions are not enough, the payload is simply too big for this budget
    // and the honest answer is false.
    const victim = ids[ids.length - 1]
    if (victim && victim !== id) {
      try {
        s.removeItem(PREFIX + victim)
      } catch {
        /* see above */
      }
      ids = ids.filter((x) => x !== victim)
    }
    try {
      s.setItem(PREFIX + id, serialised)
    } catch {
      writeIndex(s, ids.filter((x) => x !== id))
      return false
    }
  }

  writeIndex(s, ids)
  return true
}

export function remove(id: string): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(PREFIX + id)
  } catch {
    /* the index update below is what actually matters */
  }
  writeIndex(s, readIndex(s).filter((x) => x !== id))
}

/**
 * Read-through: try the network, fall back to the cache, and say which one
 * answered.
 *
 * The screens use this rather than doing it themselves, because the part that
 * gets forgotten is not the try/catch — it is re-throwing when there is no
 * cache either. A field screen must show the real error then, not an empty
 * state that looks like "this opportunity has no data".
 */
export async function readThrough<T>(
  id: string,
  fetcher: () => Promise<T>,
  now = () => Date.now(),
): Promise<{ payload: T; fromCache: boolean; savedAt: number }> {
  try {
    const payload = await fetcher()
    const at = now()
    put(id, payload, at)
    return { payload, fromCache: false, savedAt: at }
  } catch (err) {
    const cached = get<T>(id)
    if (!cached) throw err
    return { payload: cached.payload, fromCache: true, savedAt: cached.savedAt }
  }
}

/** Test seam, and the "sign out" hook: forget everything cached. */
export function clear(): void {
  const s = store()
  if (!s) return
  for (const id of readIndex(s)) {
    try {
      s.removeItem(PREFIX + id)
    } catch {
      /* best effort */
    }
  }
  try {
    s.removeItem(INDEX_KEY)
  } catch {
    /* best effort */
  }
}
